import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query'
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'

import {
  type AgentToolsApiResponse,
  type AgentToolsListParams,
  type TransformedAgentToolsResponse,
  transformAgentToolsResponse,
} from '../interfaces/agent-tool.interface'
import {
  type LlmModel,
  type LlmModelsResponse,
  transformLlmModelFromApi,
} from '../interfaces/llm-model.interface'

import { getAuthToken, getPlan, getTenantId, getUserId } from './token-provider.store'

let agentsApiBaseUrl = ''

export const setAgentsApiBaseUrl = (url: string): void => {
  agentsApiBaseUrl = url
}

export const getAgentsApiBaseUrl = (): string => agentsApiBaseUrl

const createAgentsBaseQuery = (): BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> =>
  fetchBaseQuery({
    baseUrl: agentsApiBaseUrl,
    prepareHeaders: async (headers) => {
      const token = await getAuthToken()
      if (token) {
        headers.set('Authorization', `Bearer ${token}`)
      }

      const tenantId = getTenantId()
      if (tenantId) {
        headers.set('x-tenant-id', tenantId)
      }

      const userId = getUserId()
      if (userId) {
        headers.set('x-user-id', userId)
      }

      const plan = getPlan()
      if (plan) {
        headers.set('x-plan', plan)
      }

      return headers
    },
  })

const agentsBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = (
  args,
  api,
  extraOptions
) => createAgentsBaseQuery()(args, api, extraOptions)

export interface TransformedLlmModelsResponse {
  modelList: LlmModel[]
  total: number
}

const transformModelsResponse = (response: LlmModelsResponse): TransformedLlmModelsResponse => ({
  modelList: response.models.map(transformLlmModelFromApi),
  total: response.total,
})

const buildToolsParams = (
  params: AgentToolsListParams | undefined
): Record<string, string | number> | undefined => {
  if (!params) return { page_size: 200 }
  const result: Record<string, string | number> = {
    page: params.page ?? 1,
    page_size: params.pageSize ?? 200,
  }
  if (params.category) result.category = params.category
  if (params.service) result.service = params.service
  if (params.search) result.search = params.search
  return result
}

export const agentsApiSlice = createApi({
  reducerPath: 'agentsApi',
  baseQuery: agentsBaseQuery,
  tagTypes: [
    'ChatModels',
    'MultimodalModels',
    'ImageModels',
    'AgentTools',
    'Agencies',
    'Conversations',
    'Conversation',
    'Messages',
    'PromptTemplates',
    'Connectors',
    'ConnectorProjects',
  ],
  endpoints: (builder) => ({
    getChatModels: builder.query<TransformedLlmModelsResponse, void>({
      query: () => '/api/v1/llm-models/chat',
      transformResponse: transformModelsResponse,
      providesTags: ['ChatModels'],
    }),

    getMultimodalModels: builder.query<TransformedLlmModelsResponse, void>({
      query: () => '/api/v1/llm-models/multimodal',
      transformResponse: transformModelsResponse,
      providesTags: ['MultimodalModels'],
    }),

    getImageModels: builder.query<TransformedLlmModelsResponse, void>({
      query: () => '/api/v1/llm-models/image',
      transformResponse: transformModelsResponse,
      providesTags: ['ImageModels'],
    }),

    getAgentTools: builder.query<TransformedAgentToolsResponse, AgentToolsListParams | undefined>({
      query: (params) => ({
        url: '/api/v1/tools',
        params: buildToolsParams(params),
      }),
      transformResponse: (response: AgentToolsApiResponse) => transformAgentToolsResponse(response),
      providesTags: ['AgentTools'],
    }),
  }),
})

export const {
  useGetChatModelsQuery,
  useGetMultimodalModelsQuery,
  useGetImageModelsQuery,
  useGetAgentToolsQuery,
} = agentsApiSlice
